import type { Lesson } from "@/core/types";
import { criterion, fromCriteria } from "@/core/check";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_UEBERGAENGE } from "../makros";

export const lektionUebergaenge: Lesson = {
  id: "uebergaenge",
  title: "Folienübergänge",
  summary: "Was zwischen zwei Folien passiert – und warum meistens am besten nichts.",
  durationMinutes: 7,
  learningGoals: [
    "Folienübergänge zuweisen und einheitlich halten",
    "Die Wirkung eines Übergangs auf die Wahrnehmung einschätzen",
  ],
  steps: [
    {
      id: "was-ist-ein-uebergang",
      title: "Übergang und Animation sind zwei verschiedene Dinge",
      explanation: [
        "Ein Folienübergang beschreibt, wie eine Folie die vorherige ablöst: überblenden, " +
          "schieben, wischen. Er betrifft die ganze Folie und wird auf der Registerkarte " +
          "„Übergänge“ eingestellt.",
        "Eine Animation betrifft ein einzelnes Objekt auf einer Folie: ein Stichpunkt " +
          "erscheint, ein Diagramm baut sich auf. Sie wird auf der Registerkarte " +
          "„Animationen“ eingestellt. Beides wird häufig verwechselt, obwohl es " +
          "verschiedene Werkzeuge für verschiedene Zwecke sind.",
        "Bei den Übergängen ist die Auswahl groß und der brauchbare Teil klein. " +
          "„Verblassen“ und „Schieben“ sind unauffällig und funktionieren. Alles aus der " +
          "Gruppe „Spektakulär“ – Würfel, Jalousie, Origami, Wabe – lenkt vom Inhalt ab " +
          "und wirkt nach dem dritten Mal ermüdend.",
        "Die Dauer steht rechts neben der Auswahl. Voreingestellt sind meist etwa 0,7 " +
          "Sekunden; darunter wirkt ein Übergang hektisch, darüber lässt er warten. Bei " +
          "vierzig Folien summiert sich eine Sekunde Übergang zu fast einer Minute " +
          "Wartezeit.",
      ],
      keyPoints: [
        "Übergänge betreffen die Folie, Animationen einzelne Objekte.",
        "„Verblassen“ oder „Schieben“ genügen; spektakuläre Übergänge ermüden.",
        "Übergänge → „Für alle übernehmen“ hält die Präsentation einheitlich.",
      ],
      task: {
        instruction: "Weise allen Folien einen Übergang zu – keine Folie soll ohne Übergang bleiben.",
        hint: "Übergänge → „Verblassen“ wählen → „Für alle übernehmen“.",
        macro: MAKRO_UEBERGAENGE,
        check: () =>
          pruefeMitMakro(MAKRO_UEBERGAENGE, (bericht) => {
            const folien = [...bericht.gruppe("folie").entries()];
            const ohne = folien.filter(([, folie]) => Number(folie.get("effekt") ?? 0) === 0);
            return fromCriteria(
              [
                criterion(`Die Präsentation hat Folien (gefunden: ${folien.length})`, folien.length > 0),
                criterion(
                  ohne.length === 0 ? "Jede Folie hat einen Übergang" : `Ohne Übergang: Folie ${ohne.map(([nr]) => nr).join(", ")}`,
                  folien.length > 0 && ohne.length === 0,
                ),
              ],
              {
                success: "Jede Folie hat einen Übergang.",
                failure: "Noch nicht alle Folien haben einen Übergang.",
                hint: "„Für alle übernehmen“ weist den Übergang der aktuellen Folie allen anderen zu.",
              },
            );
          }),
      },
    },
    {
      id: "einheitlich",
      title: "Einheitlich statt abwechslungsreich",
      explanation: [
        "Die Schaltfläche „Für alle übernehmen“ auf der Registerkarte „Übergänge“ ist " +
          "der wichtigste Knopf dort. Ein Übergang, der auf jeder Folie derselbe ist, " +
          "wird nach zwei Folien unsichtbar – genau das ist das Ziel. Wechselnde " +
          "Übergänge dagegen bleiben auffällig und stehlen jedes Mal Aufmerksamkeit.",
        "Eine Ausnahme lohnt sich: Abschnittstrenner dürfen einen anderen Übergang " +
          "bekommen. Der Wechsel signalisiert dann „neues Thema“ und trägt damit " +
          "Bedeutung, statt nur Effekt zu sein.",
        "Rechts auf derselben Registerkarte steht „Nächste Folie“ mit zwei Optionen: " +
          "„Bei Mausklick“ und „Nach“ mit einer Zeitangabe. Für einen Vortrag ist der " +
          "Mausklick richtig – die automatische Weiterschaltung nimmt die Kontrolle aus " +
          "der Hand. Sinnvoll ist sie nur für eine sich selbst abspielende Präsentation, " +
          "etwa an einem Messestand.",
        "Ein Sonderfall ist der Übergang „Morph“. Er bewegt Objekte, die auf zwei " +
          "aufeinanderfolgenden Folien vorkommen, weich von der einen Position zur " +
          "anderen. Damit lassen sich Abläufe zeigen, ohne mit Animationen zu arbeiten: " +
          "Folie duplizieren, Objekte verschieben, „Morph“ zuweisen. Voraussetzung ist, " +
          "dass es sich wirklich um dieselben Objekte handelt – deshalb duplizieren und " +
          "nicht neu aufbauen.",
      ],
      keyPoints: [
        "Übergänge → Für alle übernehmen.",
        "Für den Vortrag „Bei Mausklick“, nicht die automatische Weiterschaltung.",
        "„Morph“ zeigt Bewegung: Folie duplizieren, Objekte verschieben, Morph zuweisen.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass alle Folien denselben Übergang mit einer Dauer zwischen 0,3 und " +
          "1,5 Sekunden haben und per Mausklick weiterschalten.",
        hint: "Übergang und Dauer auf einer Folie einstellen, „Bei Mausklick“ anhaken, „Nach“ abhaken, dann „Für alle übernehmen“.",
        macro: MAKRO_UEBERGAENGE,
        check: () =>
          pruefeMitMakro(MAKRO_UEBERGAENGE, (bericht) => {
            const folien = [...bericht.gruppe("folie").values()];
            const effekte = new Set(folien.map((folie) => folie.get("effekt")));
            const dauern = folien.map((folie) => Number(folie.get("dauer") ?? 0));
            const automatisch = folien.filter((folie) => folie.get("zeit") === "1" || folie.get("klick") === "0");
            return fromCriteria(
              [
                criterion(`Alle Folien haben denselben Übergang (verschiedene: ${effekte.size})`, folien.length > 0 && effekte.size === 1 && !effekte.has("0")),
                criterion(
                  `Dauer zwischen 0,3 und 1,5 Sekunden (gefunden: ${[...new Set(dauern)].map((d) => d.toLocaleString("de-DE")).join(", ")})`,
                  dauern.length > 0 && dauern.every((d) => d >= 0.3 && d <= 1.5),
                ),
                criterion(`Weiter per Mausklick, ohne Zeitautomatik (abweichend: ${automatisch.length})`, automatisch.length === 0),
              ],
              {
                success: "Einheitlich, zügig, unter deiner Kontrolle – nach zwei Folien fällt der Übergang nicht mehr auf.",
                failure: "Die Übergänge sind noch nicht einheitlich eingestellt.",
                hint: "Abschnittstrenner dürfen abweichen – für diese Übung aber alle gleich.",
              },
            );
          }),
      },
    },
  ],
};
